import type {
  ReplyRating,
  ReplyReviewOutcome,
  ReplyReviewReason,
} from "@/features/ai-agent/types/reply-review";

export type SuggestionStatus = "pending" | "accepted" | "dismissed";

/** What the operator is being asked to do. `add_kb_entry` is its own kind
 *  rather than an `update_description` against business info: a knowledge base
 *  entry gets created, not a description edited. */
export const SUGGESTION_KIND = {
  ADD_KB_ENTRY: "add_kb_entry",
  UPDATE_DESCRIPTION: "update_description",
  ADD_FAQ: "add_faq",
  ADD_MEDIA: "add_media",
  CREATE_RECORD: "create_record",
  /** A change to the agent's own instructions, learned from reply reviews. */
  IMPROVE_PROMPT: "improve_prompt",
} as const;

export type SuggestionKind =
  (typeof SUGGESTION_KIND)[keyof typeof SUGGESTION_KIND];

/** What it is about. `business_info` is whatever the workspace has documented -
 *  stored as knowledge base entries either way. Only the surface the operator
 *  lands on differs by org type. */
export const SUGGESTION_TARGET = {
  BUSINESS_INFO: "business_info",
  TREATMENT: "treatment",
  DOCTOR: "doctor",
  /** The agent's instructions; `targetLabel` names the field. */
  AGENT_PROMPT: "agent_prompt",
} as const;

export type SuggestionTargetType =
  (typeof SUGGESTION_TARGET)[keyof typeof SUGGESTION_TARGET];

/** The instruction fields a prompt change can land on. */
export type AgentPromptField =
  | "goal"
  | "howYouTalk"
  | "topics"
  | "businessRules"
  | "situationalGuidance"
  | "outOfScopeInstructions"
  | "escalationTriggers";

/** How the reading pane edits a suggestion: the two-line moment, a plain line
 *  of instructions, photos to attach, or an answer with a topic. */
export type DetailEditorMode = "situational" | "prompt" | "media" | "answer";

/** Whether a queue row can be ticked for a bulk action, or carries a mark
 *  instead. */
export type QueueRowSelection = {
  isSelectable: boolean;
};

/** A contact-panel row with everything the JSX needs already decided. */
export type ContactSuggestionRow = {
  suggestion: AiSuggestion;
  heading: string;
  segments: Array<{ text: string; isBlank: boolean }>;
  summary: string;
  needsAdmin: boolean;
  canAdd: boolean;
};

/** Business info can land in a brand new entry, or on one that already covers
 *  the topic - kept alongside what is there, or replacing it. Records have no
 *  equivalent choice: a treatment FAQ is always a new FAQ on an existing
 *  treatment. */
export const DESTINATION_MODE = {
  NEW: "new",
  APPEND: "append",
  REPLACE: "replace",
} as const;

export type DestinationMode =
  (typeof DESTINATION_MODE)[keyof typeof DESTINATION_MODE];

/** Where an accepted suggestion gets written, as the operator has confirmed it.
 *  Seeded from the suggestion's own guess, then edited in the destination
 *  picker. */
export type SuggestionDestination = {
  kind: SuggestionKind;
  targetType: SuggestionTargetType;
  targetId?: string;
  mode: DestinationMode;
  targetEntryId?: string;
  /** agent_prompt only: which instruction field the line is appended to. Seeded
   *  from the generator's guess, then retargetable like any other destination. */
  promptField?: AgentPromptField;
};

export type SuggestionMedia = {
  url: string;
  type: "image" | "video";
  name?: string | null;
};

export type AiSuggestion = {
  id: string;
  kind: SuggestionKind;
  targetType: SuggestionTargetType;
  // The record's name in the model's words, or the subject for business info.
  // Advisory - it preselects the picker, the operator confirms.
  targetLabel: string | null;
  targetId: string | null;
  /** With `targetId` on a business-info suggestion: the generator found an
   *  entry that already covers this and says to write into it rather than make
   *  a new one. Advisory — it preselects the picker. */
  suggestedWriteMode?: "append" | "replace" | null;
  topic: string;
  description: string;
  customerQuestion: string | null;
  status: SuggestionStatus;
  // Prose captured from the operator's reply; null until that reply lands.
  draftedAnswer: string | null;
  // A customer-ready answer the AI wrote from its own domain knowledge, always
  // present. Facts only this business knows are left as bracketed blanks.
  suggestedAnswer: string | null;
  // That answer still has a blank to fill, so it cannot be published as-is.
  suggestedAnswerNeedsInput: boolean;
  // Attachments from that reply, kept apart from the prose so a treatment FAQ
  // does not render Markdown image syntax at a customer.
  draftedMedia: SuggestionMedia[];
  occurrenceCount: number;
  // How many different people raised it - the number worth reporting on.
  distinctContactCount: number;
  sourceContactId: string | null;
  sourceConversationId: string | null;
  sourceContactName: string | null;
  resolvedType: SuggestionTargetType | null;
  resolvedId: string | null;
  /** What produced it; `reply_reviews` rows come from the suggestion run. */
  source?: SuggestionSource;
  /** Suggestion-run rows: the generations whose reviews justified it. */
  evidence?: string[];
  suggestionRunId?: string | null;
  /** Prompt changes: set once the accepted change has been published. */
  publishedInVersionId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SuggestionSource =
  | "conversation"
  | "bulk_catalog"
  | "test_run"
  | "reply_reviews";

/** An evidence item as the reading pane renders it: labels resolved, link
 *  built, nothing left to compute in JSX. */
export type SuggestionEvidenceRow = {
  key: string;
  name: string;
  link: string | null;
  customerText: string | null;
  reply: string;
  verdict: string;
  note: string | null;
  shouldHaveRead: string | null;
  comment: string | null;
};

/** One person behind the demand tally, so the count can be opened. */
export type SuggestionAsker = {
  contactId: string;
  contactName: string | null;
  conversationId: string | null;
  askedAt: string;
};

/** One conversation behind a suggestion. */
export type SuggestionEvidenceItem = {
  generationId: string;
  contactId: string;
  contactName: string | null;
  customerText: string | null;
  reply: string;
  outcome: ReplyReviewOutcome | null;
  rating: ReplyRating | null;
  reasons: ReplyReviewReason[];
  comment: string | null;
  suggestedReply: string | null;
  steerInstruction: string | null;
};

export const ACCEPT_ACTION = {
  CREATE_KB_ENTRY: "create_kb_entry",
  WRITE_KB_ENTRY: "write_kb_entry",
  ADD_TREATMENT_FAQ: "add_treatment_faq",
  APPEND_TREATMENT_DESCRIPTION: "append_treatment_description",
  ADD_TREATMENT_MEDIA: "add_treatment_media",
  ADD_DOCTOR_FAQ: "add_doctor_faq",
  APPEND_DOCTOR_DESCRIPTION: "append_doctor_description",
  APPLY_PROMPT_CHANGE: "apply_prompt_change",
} as const;

export type AcceptAction = (typeof ACCEPT_ACTION)[keyof typeof ACCEPT_ACTION];

export type AcceptSuggestionPayload = {
  id: string;
  action: AcceptAction;
  // What gets written to: the KB entry for write_kb_entry, the record otherwise.
  targetId?: string;
  entryWriteMode?: "append" | "replace";
  // Overrides that replace the AI's wording before it lands. `answer` is the
  // prose for every destination, so the backend has one field to gate on.
  topic?: string;
  answer?: string;
  // apply_prompt_change only. Omitted, the backend keeps the field the
  // generator named on the row.
  promptField?: AgentPromptField;
};

/** Outcome of "Approve all ready drafts". `skipped` is the honest half: those
 *  are left pending on purpose, not lost. */
export type AcceptReadyResult = {
  accepted: number;
  skipped: number;
  failed: number;
};

export type AcceptSuggestionResult = {
  resolvedType: SuggestionTargetType;
  resolvedId: string;
  faqId?: string;
};

/** Outcome of publishing or dismissing a selection from the queue. Each row is
 *  its own request, so a failure part-way leaves the rest applied. */
export type BulkSuggestionResult = {
  succeeded: number;
  failed: number;
};
